import { formatBoolean } from '@application/utils';
import { ProfessionalTypeEnum } from '@gql/graphql';
import { Button, PrebookingWarning } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import type { FC, ReactNode } from 'react';
import { Fragment, useMemo } from 'react';
import * as Styled from '../styled';
import type { BookingPageFormValues, BookingPageProps } from '../types';
import { BookingPageRecap } from './BookingPageRecap';

export const BookingPageSummary: FC<BookingPageProps> = (props) => {
  const { onEditInfoClick, preBookingWarning, pro } = props;
  const { translate } = useTranslation();
  const { submitForm, values, isSubmitting } = useFormikContext<BookingPageFormValues>();

  const isGroomer = useMemo(() => {
    return pro.type === ProfessionalTypeEnum.GROOMER;
  }, [pro.type]);

  const summaryButtons = useMemo((): ReactNode => {
    let notEnoughData = !values.bookingReason || !values.animalIds;
    if (typeof values.animalIds === 'object' && !values.animalIds.length) {
      notEnoughData = true;
    }
    return (
      <Styled.SummaryButtonContainer>
        <Button
          disabled={notEnoughData || isSubmitting}
          color="lightBlue"
          onClick={onEditInfoClick}
        >
          {translate('common.buttons.editInformations')}
        </Button>
        <Button disabled={notEnoughData} color="blue" onClick={submitForm} loading={isSubmitting}>
          {translate('common.buttons.confirm')}
        </Button>
      </Styled.SummaryButtonContainer>
    );
  }, [
    isSubmitting,
    onEditInfoClick,
    submitForm,
    translate,
    values.animalIds,
    values.bookingReason,
  ]);

  return (
    <Styled.SummaryCardContainer>
      {summaryButtons}
      <BookingPageRecap {...props} />

      <Styled.SummaryWrapper>
        {preBookingWarning && <PrebookingWarning {...preBookingWarning} />}
        <Styled.SummaryInfoTitle>
          {translate('booking.summary.informationTitle')}
        </Styled.SummaryInfoTitle>

        <Styled.Label>{translate('glossary.visit')}</Styled.Label>
        <Styled.Value>{values.bookingReason}</Styled.Value>

        {/* values.animal INFO */}

        {(values.animals ?? []).map((animal, index) => {
          return (
            <Fragment key={index}>
              <Styled.SummaryInfoTitleAnimal>{`${translate('glossary.animal')} ${(
                index + 1
              ).toString()}: ${animal.name}`}</Styled.SummaryInfoTitleAnimal>
              <Styled.Label>{translate('glossary.typeOfAnimal')}</Styled.Label>
              <Styled.Value>
                {animal.type ? translate(`enum.animalType.${animal.type}`) : '-'}
              </Styled.Value>
              <Styled.Label>{translate('glossary.birthday')}</Styled.Label>
              <Styled.Value>
                {animal.birthday ? dayjs(animal.birthday)?.format?.('DD/MM/YYYY') ?? '-' : '-'}
              </Styled.Value>
              <Styled.Label>{translate('glossary.gender')}</Styled.Label>
              <Styled.Value>
                {animal.gender ? translate(`enum.gender.${animal.gender}`) : '-'}
              </Styled.Value>
              <Styled.Label>{translate('glossary.neuter')}</Styled.Label>
              <Styled.Value>{formatBoolean(translate, animal.sterilized) ?? '-'}</Styled.Value>
            </Fragment>
          );
        })}

        {/* GROOMER INFO */}

        {isGroomer && (
          <>
            <Styled.Label>{translate('glossary.recentlyAdopted')}</Styled.Label>
            <Styled.Value>
              {formatBoolean(translate, values.animalRecentlyAdopted) ?? '-'}
            </Styled.Value>

            <Styled.Label>{translate('glossary.firstVisit')}</Styled.Label>
            <Styled.Value>
              {formatBoolean(translate, values.animalFirstTimeAtGroomer) ?? '-'}
            </Styled.Value>

            <Styled.Label>{translate('glossary.character').split(' (')[0]}</Styled.Label>
            <Styled.Value>
              {values.animalTemper ? translate(`enum.temperType.${values.animalTemper}`) : '-'}
            </Styled.Value>

            <Styled.Label>{translate('glossary.coatCondition')}</Styled.Label>
            <Styled.Value>
              {values.animalHasKnottedFur
                ? translate(`enum.yesNoUnknownStatus.${values.animalHasKnottedFur}`)
                : '-'}
            </Styled.Value>

            <Styled.Label>{translate('glossary.lastVisit')}</Styled.Label>
            <Styled.Value>
              {values.animalLastVisit
                ? translate(`enum.lastVisitType.${values.animalLastVisit}`)
                : '-'}
            </Styled.Value>

            <Styled.Label>{translate('glossary.brushing')}</Styled.Label>
            <Styled.Value>
              {values.animalBrushingFrequency
                ? translate(`enum.frequencyType.${values.animalBrushingFrequency}`)
                : '-'}
            </Styled.Value>
          </>
        )}
      </Styled.SummaryWrapper>
      {summaryButtons}
    </Styled.SummaryCardContainer>
  );
};
